<template>
  <!-- Wrapper for Desktop Sidebar and its Toggle Button -->
  <div v-if="shouldShowDesktopAside" class="desktop-sidebar-wrapper">
    <aside
      class="dashboard-sidebar"
      :class="{ 'is-collapsed': isSidebarCollapsed }"
    >
      <div class="sidebar-top">
        <div class="sidebar-brand" @click="handleMenuNavigate(navItems[0])">
          <template v-if="corporateLogo">
            <img
              v-if="!isSidebarCollapsed"
              :src="corporateLogoDisplay"
              alt="Corporate Logo"
              class="sidebar-logo"
            />
            <img
              v-else
              :src="corporateLogoDisplay"
              alt="Corporate Logo"
              class="sidebar-logo--compact"
            />
          </template>
          <div v-else class="sidebar-logo-skeleton" :class="{ 'sidebar-logo-skeleton--compact': isSidebarCollapsed }"></div>
        </div>
      </div>

      <nav class="sidebar-menu">
        <button
          v-for="item in filteredNavItems"
          :key="item.link_name"
          type="button"
          class="sidebar-menu-item"
          :class="{ active: currentRoute === item.link_spesific }"
          @click="handleMenuNavigate(item)"
        >
          <span class="sidebar-menu-icon" :style="{ WebkitMaskImage: `url(${currentRoute === item.link_spesific ? item.activeIcon : item.icon})`, maskImage: `url(${currentRoute === item.link_spesific ? item.activeIcon : item.icon})` }" :aria-label="item.link_name"></span>
          <span v-if="!isSidebarCollapsed">{{ item.link_name }}</span>
        </button>
      </nav>

      <div class="sidebar-footer">
        <div class="sidebar-user" v-if="memberName || dashboard?.member_name" @click="goToProfile">
          <div class="sidebar-avatar" :class="{ 'is-placeholder': !memberAvatarDisplay }">
            <img v-if="memberAvatarDisplay" :src="memberAvatarDisplay" alt="Member avatar" />
            <span v-else>{{ userInitials }}</span>
          </div>
          <div class="sidebar-user-info" v-if="!isSidebarCollapsed">
            <p class="sidebar-member" :title="memberName || dashboard?.member_name">{{ memberName || dashboard?.member_name }}</p>
            <span class="sidebar-tier" :title="tierName && tierName !== '-' ? tierName : 'Member'">{{ tierName && tierName !== '-' ? tierName : 'Member' }}</span>
            <span class="sidebar-corporate" v-if="corporateName" :title="corporateName">{{ corporateName }}</span>
          </div>
        </div>
        <div class="sidebar-actions">
          <button v-if="!isSidebarCollapsed" class="sidebar-policy-text" @click="openPolicyModal">
            <svg class="policy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 10.6667V8M8 5.33333H8.00667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>View policies</span>
          </button>
          

          <button v-if="!isSidebarCollapsed" class="sidebar-policy-text" @click="openContactInfoModal">
            <svg class="policy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 10.6667V8M8 5.33333H8.00667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Contact Info</span>
          </button>
  
          <button class="sidebar-logout-btn" @click="handleLogout">
            <svg class="logout-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6M10.6667 11.3333L14 8M14 8L10.6667 4.66667M14 8H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span v-if="!isSidebarCollapsed">Logout</span>
          </button>
        </div>
      </div>
    </aside>

    <!-- Toggle button is now a sibling to the aside, positioned absolutely -->
    <button
      type="button"
      class="sidebar-toggle"
      :class="{ 'is-collapsed': isSidebarCollapsed }"
      @click="toggleSidebar"
      aria-label="Toggle sidebar"
    >
      <span v-if="isSidebarCollapsed">&raquo;</span>
      <span v-else>&laquo;</span>
    </button>
  </div>

  <!-- Desktop Policy Modal - Outside wrapper for proper z-index -->
  <o-modal
    v-if="shouldShowDesktopAside && showDesktopPolicyModal"
    @close="closeDesktopPolicyModal"
    :modalClass="'show d-block policy-modal-desktop'"
    :bodyClass="'p-0'"
    :allow-html="false"
    :is-footer-visible="false"
  >
    <template #header>
      <h5 class="modal-title">Policies</h5>
    </template>
    <template #body>
      <div class="policies-container" style="height: 450px; max-height: 450px; min-height: 450px; overflow: hidden; display: flex; flex-direction: column; color: black; word-wrap: break-word;">
        <div class="policies-tabs">
          <button class="tab-button" :class="{ active: desktopActiveTab === 'privacy' }" @click="desktopActiveTab = 'privacy'">Privacy Policy</button>
          <button class="tab-button" :class="{ active: desktopActiveTab === 'terms' }" @click="desktopActiveTab = 'terms'">Terms & Conditions</button>
        </div>
        <div class="tab-content" style="height: 450px; max-height: 450px; min-height: 450px; overflow-y: auto; padding: 1.5rem 1.5rem 2.5rem 1.5rem; flex: 1;">
          <div v-if="desktopActiveTab === 'privacy'" class="policy-content">
            <PublicPolicyDisplay type="privacy" />
          </div>
          <div v-if="desktopActiveTab === 'terms'" class="policy-content">
            <PublicPolicyDisplay type="terms" />
          </div>
        </div>
      </div>
    </template>
  </o-modal>

   <!-- Desktop Contact Info - Outside wrapper for proper z-index -->
  <o-modal
    v-if="shouldShowDesktopAside && showDesktopContactInfoModal"
    @close="closeDesktopContactInfoModal"
    :modalClass="'show d-block policy-modal-desktop'"
    :bodyClass="'p-0'"
    :allow-html="false"
    :is-footer-visible="false"
  >
    <template #header>
      <h5 class="modal-title"> Contact Info</h5>
    </template>
    <template #body>
      <div class="policies-container" style="height: 450px; max-height: 450px; min-height: 450px; overflow: hidden; display: flex; flex-direction: column; color: black; word-wrap: break-word;">
        <div class="tab-content" style="height: 450px; max-height: 450px; min-height: 450px; overflow-y: auto; padding: 1.5rem 1.5rem 2.5rem 1.5rem; flex: 1;">
          <div class="member-details" v-if="corporateContactInfo">
  
                <div class="detail-item">
                  <span class="detail-label">Corporate Name</span>
                  <span class="detail-value"
                  v-if="corporateName">{{ corporateName }}</span>
                  
                </div>

                 <div class="detail-item">
                  <span class="detail-label">Phone</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.phone }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">Address</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.address }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">State</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.state }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">Postcode</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.postcode }}
                  </span>
                </div>

                <div class="detail-item" v-if="corporateContactInfo.fax">
                  <span class="detail-label">Fax</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.fax }}
                  </span>
                </div>

              </div>

              <div v-else>
                <p>Contact information not available</p>
              </div>

        </div>
      </div>
    </template>
  </o-modal>

  <!-- Mobile (and desktop for non-dashboard pages) bottom navbar -->
  <div v-if="shouldShowMobileNav">
    <div v-if="isLoading" class="d-flex justify-content-center spinner-border text-primary spinner-position form"></div>
    <div v-if="isLoading" class="spinner"></div>

    <o-qr-code-member-modal
      :isVisible="qrCodeModal"
      :corporateLogo="corporateLogo"
      :memberId="memberId"
      :memberName="memberName"
      :corporateName="corporateName"
      :tierName="tierName"
      :tierIcon="tierIcon"
      :memberAvatar="memberAvatar"
      :memberVouchers="memberVouchers"
      :memberPoints="memberPoints"
      @close="qrCodeModal = false"
    />

    <div>
      <o-user-info-mobile 
        v-if="shouldShowUserInfoMobile"
        class="mt-4 position-absolute custom-position-user-info" 
        @showPrivacyPolicy="showPoliciesModal" 
        @openContactInfo="openMobileContactInfo"
      />
      <div class="navbar-container">
        <div class="d-inline-flex d-flex buttom-link" v-for="list in filteredNavItems" :key="list.link_name">
          <!-- QR Menu: Show modal instead of navigating -->
          <button
            v-if="list.link_name === 'Member QR'"
            type="button"
            class="nav-link nav-link-btn"
            :class="{ 'active-link': qrCodeModal }"
            @click="showQrCode()"
          >
            <div>
              <span class="nav-icon" :style="{ WebkitMaskImage: `url(${qrCodeModal ? list.activeIcon : list.icon})`, maskImage: `url(${qrCodeModal ? list.activeIcon : list.icon})` }" :aria-label="list.link_name"></span>
            </div>
            <span class="nav-title" v-if="qrCodeModal">{{ list.link_name }}</span>
          </button>
          <!-- Other menu items: Use router-link -->
          <router-link
            v-else
            :to="list.link_spesific"
            class="nav-link"
            :class="{ 'active-link': $route.path === list.link_spesific }"
            aria-current="page"
          >
            <div>
              <span class="nav-icon" :style="{ WebkitMaskImage: `url(${$route.path === list.link_spesific ? list.activeIcon : list.icon})`, maskImage: `url(${$route.path === list.link_spesific ? list.activeIcon : list.icon})` }" :aria-label="list.link_name"></span>
            </div>
            <span class="nav-title" v-if="$route.path === list.link_spesific">{{ list.link_name }}</span>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Mobile Policies Modal -->
    <o-modal
      v-if="showPrivacyModal"
      @close="closePrivacyModal"
      :modalClass="'show d-block'"
      :bodyClass="'p-0'"
      :allow-html="false"
      :is-footer-visible="false"
    >
      <template #header>
        <h5 class="modal-title">Policies</h5>
      </template>
      <template #body>
        <div
          class="policies-container"
          style="height: 450px; max-height: 450px; min-height: 450px; overflow: hidden; display: flex; flex-direction: column; color: black; word-wrap: break-word;"
        >
          <div class="policies-tabs">
            <button class="tab-button" :class="{ active: activeTab === 'privacy' }" @click="activeTab = 'privacy'">Privacy Policy</button>
            <button class="tab-button" :class="{ active: activeTab === 'terms' }" @click="activeTab = 'terms'">Terms & Conditions</button>
          </div>
          <div class="tab-content" style="height: 450px; max-height: 450px; min-height: 450px; overflow-y: auto; padding: 1.5rem 1.5rem 2.5rem 1.5rem; flex: 1;">
            <div v-if="activeTab === 'privacy'" class="policy-content">
              <PublicPolicyDisplay type="privacy" />
            </div>
            <div v-if="activeTab === 'terms'" class="policy-content">
              <PublicPolicyDisplay type="terms" />
            </div>
          </div>
        </div>
      </template>
    </o-modal>

    <!-- Mobile Contact Info Modal -->
      <o-modal
      v-if="showContactInfoModal"
      @close="closeContactInfoModal"
      :modalClass="'show d-block'"
      :bodyClass="'p-0'"
      :allow-html="false"
      :is-footer-visible="false"
    >
    <template #header>
        <h5 class="modal-title">Contact Info</h5>
      </template>
     <template #body>
      <div class="policies-container" style="height: 450px; max-height: 450px; min-height: 450px; overflow: hidden; display: flex; flex-direction: column; color: black; word-wrap: break-word;">
        <div class="tab-content" style="height: 450px; max-height: 450px; min-height: 450px; overflow-y: auto; padding: 1.5rem 1.5rem 2.5rem 1.5rem; flex: 1;">
          <div class="member-details" v-if="corporateContactInfo">
  
                <div class="detail-item">
                  <span class="detail-label">Corporate Name</span>
                  <span class="detail-value"
                  v-if="corporateName">{{ corporateName }}</span>
                  
                </div>

                <div class="detail-item">
                  <span class="detail-label">Phone</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.phone }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">Address</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.address }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">State</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.state }}
                  </span>
                </div>

                <div class="detail-item">
                  <span class="detail-label">Postcode</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.postcode }}
                  </span>
                </div>

                <div class="detail-item" v-if="corporateContactInfo.fax">
                  <span class="detail-label">Fax</span>
                  <span class="detail-value">
                    {{ corporateContactInfo.fax }}
                  </span>
                </div>

              </div>

              <div v-else>
                <p>Contact information not available</p>
              </div>

        </div>
      </div>
    </template>
    </o-modal>

  </div>
</template>

<script>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Capacitor } from '@capacitor/core';
import { setSwipeBackEnabled } from '@/services/swipeBack';
import { OUserInfoMobile, OQrCodeMemberModal, OModal } from '@/components/OmnihControl';
import PublicPolicyDisplay from '@/components/PublicPolicyDisplay.vue';
import { eventBus } from '@/eventBus';
import { usePointSystem } from '@/composables/pointsystem';
import { useModalHistory } from '@/composables/useModalHistory';

export default {
  name: 'NavbarMember',
  props: {
    // When used inside MemberDashboardDesktop, dashboard data is provided for displaying member name
    dashboard: {
      type: Object,
      default: () => ({}),
    },
    // Optional: force show desktop aside regardless of route (used by MemberDashboardDesktop)
    forceDesktopAside: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    OUserInfoMobile,
    OQrCodeMemberModal,
    OModal,
    PublicPolicyDisplay,
  },
  setup(props, { emit }) {
    const { isPointSystemActive } = usePointSystem();
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    // Viewport detection
    const isDesktop = ref(window.innerWidth >= 992);
    const updateViewport = () => {
      isDesktop.value = window.innerWidth >= 992;
    };
    onMounted(() => {
      updateViewport();
      window.addEventListener('resize', updateViewport);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateViewport);
    });

    const isDashboardRoute = computed(() => route.path === '/client/dashboard');
    // Preserve previous behavior of wrapper: show desktop aside on non-dashboard pages when on desktop
    const shouldShowDesktopAside = computed(() => (props.forceDesktopAside ? isDesktop.value : isDesktop.value && !isDashboardRoute.value));
    // Show mobile bottom nav only on mobile devices, not on desktop
    const shouldShowMobileNav = computed(() => !isDesktop.value);

    const isNativeApp = Capacitor.isNativePlatform();

    // Member info
    const memberId = ref('');
    const memberName = ref('');
    const corporateName = ref('');
    const corporateLogo = ref('');
    const tierName = ref('');
    const tierIcon = ref('');
    const memberAvatar = ref('');

    const userStore = computed(() => store.state.Auth.currUser);
    const corporateLogoDisplay = computed(() => corporateLogo.value || '/img/member-icon/tier-logo.svg');
    const memberAvatarDisplay = computed(() => memberAvatar.value || '');
    const userInitials = computed(() => {
      const name = memberName.value || props.dashboard?.member_name || '';
      if (!name) return '?';
      return name
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    });

    const loadMemberProfile = (member) => {
      memberId.value = member?.member?.id || '';
      memberName.value = `${member?.title || member?.member?.title || ''} ${member?.first_name || member?.member?.first_name || ''} ${member?.last_name || member?.member?.last_name || ''}`.trim();
      corporateName.value = member?.member?.corporate_name || '';
      corporateLogo.value = member?.member?.corporate_logo || '';
      tierName.value = member?.member?.tier_name || '';
      tierIcon.value = member?.member?.tier?.icon?.image_url || store.state.ClientDashboard?.dashboard?.tier?.icon?.image_url || '';
      memberAvatar.value = member?.member?.member_avatar || '';
    };

    // Watch for changes in ClientDashboard store
    watch(
      () => store.state.ClientDashboard?.dashboard,
      (newDashboard) => {
        if (newDashboard?.tier?.icon?.image_url) {
           tierIcon.value = newDashboard.tier.icon.image_url;
        }
      },
      { deep: true, immediate: true }
    );

    // Mobile-specific loading state needs to be defined BEFORE any watchers that use it
    const isLoading = ref(true);

    watch(
      userStore,
      (newVal) => {
        if (newVal?.member) {
          loadMemberProfile(newVal);
          const corporateId = newVal.member.corporate_id;
          if (corporateId) {
            store.dispatch('FeatureFlag/fetchFeatureFlag', corporateId)
              .catch(error => {
                console.error('Error fetching feature flag in NavbarMember:', error);
              });
          }
        }
        isLoading.value = false;
      },
      { immediate: true, deep: true }
    );

    // Desktop aside specific
    const currentRoute = computed(() => route.path);
    const isSidebarCollapsed = ref(false);
    const toggleSidebar = () => {
      isSidebarCollapsed.value = !isSidebarCollapsed.value;
      emit('sidebar-toggled', isSidebarCollapsed.value);
    };

    const navItems = ref([
      {
        link_name: 'Dashboard',
        link_spesific: '/client/dashboard',
        icon: '/img/member-icon/dashboard-normal.svg',
        activeIcon: '/img/member-icon/dashboard-active.svg',
      },
      {
        link_name: 'My Transactions',
        link_spesific: '/client/transaction-history',
        icon: '/img/member-icon/transaction-normal.svg',
        activeIcon: '/img/member-icon/transaction-active.svg',
      },
      {
        link_name: 'My Vouchers',
        link_spesific: '/client/voucher',
        icon: '/img/member-icon/voucher-normal.svg',
        activeIcon: '/img/member-icon/voucher-active.svg',
      },
      {
        link_name: 'Deals',
        link_spesific: '/client/deals',
        icon: '/img/member-icon/deals-normal.svg',
        activeIcon: '/img/member-icon/deals-active.svg',
      },
      {
        link_name: 'Member QR',
        link_spesific: '/client/qrcode',
        icon: '/img/member-icon/qr-normal.svg',
        activeIcon: '/img/member-icon/qr-active.svg',
      },
    ]);
    

    const filteredNavItems = computed(() => {
      let items = navItems.value;

      if (!isPointSystemActive.value) {
        // Hide My Vouchers and Deals when point system is NONACTIVE
        items = items.filter(item => 
          item.link_name !== 'My Vouchers' && item.link_name !== 'Deals'
        );
      }


      return items;
    });

    const handleMenuNavigate = (item) => {
      if (item.link_name === 'Member QR') {
        // Let parent control QR modal on desktop
        emit('open-qr');
        return;
      }
      router.push(item.link_spesific);
    };

    const goToProfile = () => {
      if (Capacitor.isNativePlatform()) {
        window.location.href = '/client/benefit';
      } else {
        router.push('/client/benefit');
      }
    };

    const handleLogout = async () => {
      try {
        const isMemberUser = store.state.Auth.currUser?.level === 'member';
        const newAuthFlag = store.getters['FeatureFlag/getNewAuthModuleFlag'];

        await store.dispatch('Auth/logout');
        
        // Disable swipe-back immediately — prevent flash of authenticated page during transition
        await setSwipeBackEnabled(false);

        // Use router.replace (not window.location) on native — window.location causes full reload which re-shows splash screen
        if (newAuthFlag === 'ACTIVE') {
          if (isMemberUser) {
            router.replace('/member/login');
          } else {
            router.replace('/backoffice/login');
          }
        } else {
          router.replace('/login');
        }
      } catch (error) {
        console.error('Logout error:', error);
        await setSwipeBackEnabled(false);
        // Always redirect even if logout fails
        router.replace('/login');
      }
    };

    // Desktop policy modal
    const showDesktopPolicyModal = ref(false);
    const desktopActiveTab = ref('privacy');

    const { pushModalHistory: pushDesktopPolicyModal, popModalHistory: popDesktopPolicyModal } = useModalHistory({
      isVisible: showDesktopPolicyModal,
      onClose: () => { showDesktopPolicyModal.value = false; },
      stateKey: 'desktop-policy-modal',
    });

    const openPolicyModal = () => {
      showDesktopPolicyModal.value = true;
      pushDesktopPolicyModal();
    };

    const closeDesktopPolicyModal = () => {
      showDesktopPolicyModal.value = false;
      popDesktopPolicyModal();
    };

    // Desktop Contact Info modal
    const showDesktopContactInfoModal = ref(false);
    const desktopContactActiveTab = ref('privacy');

    const { pushModalHistory: pushDesktopContactModal, popModalHistory: popDesktopContactModal } = useModalHistory({
      isVisible: showDesktopContactInfoModal,
      onClose: () => { showDesktopContactInfoModal.value = false; },
      stateKey: 'desktop-contact-modal',
    });

    const openContactInfoModal = () => {
      showDesktopContactInfoModal.value = true;
      pushDesktopContactModal();
    };

    const closeDesktopContactInfoModal = () => {
      showDesktopContactInfoModal.value = false;
      popDesktopContactModal();
    };

    // Mobile Contact Info modal
    const showContactInfoModal = ref(false);

    const { pushModalHistory: pushMobileContactModal, popModalHistory: popMobileContactModal } = useModalHistory({
      isVisible: showContactInfoModal,
      onClose: () => { showContactInfoModal.value = false; },
      stateKey: 'mobile-contact-modal',
    });

    const closeContactInfoModal = () => {
      showContactInfoModal.value = false;
      popMobileContactModal();
    };

    const openMobileContactInfo = () => {
      showContactInfoModal.value = true;
      pushMobileContactModal();
    };

     //Mengambil Busines Profile
    const corporateContactInfo = computed(() => {
  return store.state.ClientDashboard.dashboard?.branch_images?.[0]?.business_profile || null
})

    

    // Mobile-specific state and actions
    const qrCodeModal = ref(false);
    const showPrivacyModal = ref(false);
    const activeTab = ref('privacy');

    const { pushModalHistory: pushMobilePrivacyModal, popModalHistory: popMobilePrivacyModal } = useModalHistory({
      isVisible: showPrivacyModal,
      onClose: () => { showPrivacyModal.value = false; },
      stateKey: 'mobile-privacy-modal',
    });

    const showQrCode = async () => {
      // Ensure we have tier icon
      
      // Try to get from store state first if not already set
      if (!tierIcon.value && store.state.ClientDashboard?.dashboard?.tier?.icon?.image_url) {
        tierIcon.value = store.state.ClientDashboard.dashboard.tier.icon.image_url;
      }

      if (!tierIcon.value) {
        try {
           const res = await store.dispatch('ClientDashboard/read');
           
           if (res?.data?.data?.tier?.icon?.image_url) {
             tierIcon.value = res.data.data.tier.icon.image_url;
           } else {
             // console.log('NavbarMember dashboard data missing tier icon in API response. Tier data:', res?.data?.data?.tier);
           }
        } catch (e) {
           // console.error('Failed to fetch dashboard for QR icon', e);
        }
      }
      qrCodeModal.value = true;
      eventBus.emit('navigation-triggered', 'navbar-mobile.vue');
    };
    const showPoliciesModal = async () => {
      showPrivacyModal.value = true;
      pushMobilePrivacyModal();
    };
    const closePrivacyModal = () => {
      showPrivacyModal.value = false;
      popMobilePrivacyModal();
    };
    const getIcon = (menuItem) => {
      return route.path === menuItem.link_spesific ? menuItem.activeIcon : menuItem.icon;
    };

    // Hide user info mobile on specific routes
    const shouldShowUserInfoMobile = computed(() => {
      const hiddenRoutes = ['/client/benefit', '/client/account'];
      return !hiddenRoutes.includes(route.path);
    });

    // Computed untuk vouchers & points dari ClientDashboard store (ter-refresh setelah redeem/cancel)
    const memberVouchers = computed(() => {
      return store.state.ClientDashboard?.dashboard?.owned_vouchers ?? store.state.Auth.userInfo?.member?.filtered_vouchers?.length ?? 0;
    });
    
    const memberPoints = computed(() => {
      return store.state.ClientDashboard?.dashboard?.points ?? store.state.Auth.userInfo?.member?.points ?? 0;
    });

    return {
      // route/context
      router,
      route,
      isDesktop,
      isDashboardRoute,
      shouldShowDesktopAside,
      shouldShowMobileNav,
      isNativeApp,
      // member info
      memberId,
      memberName,
      corporateName,
      corporateLogo,
      corporateLogoDisplay,
      tierName,
      tierIcon,
      memberAvatar,
      memberAvatarDisplay,
      userInitials,
      userStore,
      memberVouchers,
      memberPoints,
      // aside
      navItems,
      filteredNavItems,
      currentRoute,
      isSidebarCollapsed,
      toggleSidebar,
      handleMenuNavigate,
      // bottom nav
      isLoading,
      qrCodeModal,
      showQrCode,
      showPrivacyModal,
      showPoliciesModal,
      closePrivacyModal,
      activeTab,
      getIcon,
      goToProfile,
      handleLogout,
      showDesktopPolicyModal,
      desktopActiveTab,
      openPolicyModal,
      closeDesktopPolicyModal,
      shouldShowUserInfoMobile,
      showDesktopContactInfoModal,
      desktopContactActiveTab,
      openContactInfoModal,
      closeDesktopContactInfoModal,
      corporateContactInfo,
      showContactInfoModal,
      closeContactInfoModal,
      openMobileContactInfo,
    };
  },
};
</script>

<style scoped>
/* =====================
   Mobile bottom navbar
   (from NavbarMemberMobile.vue)
   ===================== */
.navbar-container {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 32px 16px;
  background-color: white;
  box-shadow: 0px -2px 5px rgba(0, 0, 0, 0.1);
  z-index: 999;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  gap: 6px;
  border-radius: 32px;
  text-decoration: none;
  color: #888;
  transition: all 0.3s ease;
  white-space: nowrap;
}

/* Button variant of nav-link for QR menu */
.nav-link-btn {
  border: none;
  background: transparent;
  cursor: pointer;
}

.nav-title {
  color: var(--primary-color, #F93E3E);
  font-size: 12px;
  font-weight: 600;
}

.active-link {
  background-color: var(--secondary-color, #FEE9E9);
  color: var(--primary-color, #F93E3E);
}

.active-link .nav-icon {
  background-color: var(--primary-color, #F93E3E);
}

.nav-icon {
  display: inline-block;
  width: 24px;
  height: 24px;
  background-color: currentColor;
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
}

.nav-link > div {
  display: flex;
  align-items: center;
}

.buttom-link {
  text-align: center;
  align-items: center;
}

.custom-position-user-info {
  position: absolute;
  top: 0px;
  right: 12px;
  z-index: 101;
}

.policies-container {
  height: 450px;
  display: flex;
  flex-direction: column;
}

.policies-tabs {
  display: flex;
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.tab-button {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  background-color: transparent;
  color: #6c757d;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
}

.tab-button:hover {
  background-color: #e9ecef;
  color: #495057;
}

.tab-button.active {
  color: var(--primary-color, #F93E3E);
  background-color: #fff;
  border-bottom-color: var(--primary-color, #F93E3E);
  font-weight: 600;
}

.tab-content {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.policy-content {
  padding-bottom: 1.5rem;
}

/* =====================
   Desktop aside navbar
   (from NavbarMemberDesktop.vue)
   ===================== */

.desktop-sidebar-wrapper {
  position: sticky;
  top: 0;
  height: 100vh;
  /* The width of this wrapper is controlled by the grid layout in MemberDashboardDesktop.vue */
}

.dashboard-sidebar {
  position: sticky;
  top: 0;
  height: 100vh;
  width: 280px; /* Default expanded width */
  background: #ffffff;
  color: #1B1E28;
  display: flex;
  flex-direction: column;
  padding: 24px 20px;
  gap: 32px; /* Increased gap */
  transition: width 0.3s ease, padding 0.3s ease;
  overflow-y: auto;
  overflow-x: hidden; /* Re-enabled for good practice */
  border-right: 1px solid #e2e8f0;
}

.dashboard-sidebar.is-collapsed {
  width: 92px;
  padding: 24px 12px;
  align-items: center;
}

.sidebar-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
}

.dashboard-sidebar.is-collapsed .sidebar-top {
  flex-direction: column;
  gap: 20px;
  justify-content: center;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.dashboard-sidebar.is-collapsed .sidebar-brand {
  justify-content: center;
}

.sidebar-logo {
  width: 136px;
  height: auto;
  object-fit: contain;
}

.sidebar-logo--compact {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
}

.sidebar-toggle {
  position: absolute;
  top: 24px;
  left: 264px; /* Initial position (280px sidebar width - 16px) */
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #717784;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

.sidebar-toggle.is-collapsed {
  left: 76px; /* Collapsed position (92px sidebar width - 16px) */
}

.sidebar-toggle:hover {
  background: #f5f6fa;
  color: var(--primary-color, #F93E3E);
  border-color: var(--primary-color, #F93E3E);
}

.sidebar-user {
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 0 4px;
  cursor: pointer;
  border-radius: 12px;
  transition: background 0.2s ease;
}

.sidebar-user:hover {
  background: #f5f6fa;
}

.dashboard-sidebar.is-collapsed .sidebar-user {
  justify-content: center;
  padding: 0;
}

.sidebar-avatar {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 50%; /* Perfect circle */
  background: var(--secondary-color, #FEE9E9);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-weight: 600;
  font-size: 18px;
  text-transform: uppercase;
  color: var(--primary-color, #F93E3E);
  transition: transform 0.3s ease;
}

.sidebar-avatar:hover {
  transform: scale(1.1);
}

.sidebar-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sidebar-user-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.sidebar-member {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #1B1E28;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.sidebar-tier,
.sidebar-corporate {
  font-size: 12px;
  color: #717784;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.sidebar-menu {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  width: 100%;
}

.sidebar-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  background: transparent;
  color: #717784;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, color 0.2s ease;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
}

.sidebar-menu-item:hover {
  background: #f5f6fa;
  transform: translateX(4px);
  color: #1B1E28;
}

.dashboard-sidebar.is-collapsed .sidebar-menu-item {
  justify-content: center;
  padding: 12px;
}

.dashboard-sidebar.is-collapsed .sidebar-menu-item:hover {
  transform: translateX(0);
}

.sidebar-menu-item.active {
  background: var(--secondary-color, #FEE9E9);
  color: var(--primary-color, #F93E3E);
  font-weight: 600;
}

.sidebar-menu-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background-color: currentColor;
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
}

.dashboard-sidebar.is-collapsed .sidebar-menu-icon {
  width: 24px;
  height: 24px;
}

.sidebar-footer {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: auto;
  width: 100%;
}

.sidebar-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.sidebar-policy-text {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #717784;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  text-align: left;
}

.sidebar-policy-text:hover {
  background-color: #f5f6fa;
  color: var(--primary-color, #F93E3E);
}

.policy-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.sidebar-logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background-color: var(--secondary-color, #FEE9E9);
  color: var(--primary-color, #F93E3E);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  text-align: left;
}

.sidebar-logout-btn:hover {
  background-color: var(--secondary-color, #FEE9E9);
  transform: translateY(-1px);
}

.sidebar-logout-btn:active {
  transform: translateY(0);
}

.logout-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.dashboard-sidebar.is-collapsed .sidebar-policy-text {
  display: none;
}

.dashboard-sidebar.is-collapsed .sidebar-logout-btn {
  padding: 12px;
  justify-content: center;
}

.sidebar-logo-skeleton {
  width: 136px;
  height: 40px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.sidebar-logo-skeleton--compact {
  width: 48px;
  height: 48px;
  border-radius: 12px;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* ===== CONTACT INFO ===== */

.member-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

/* Setiap baris info */
.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 16px;
  border-bottom: 1px solid #E1E4EA;
}

.detail-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

/* Label (judul kecil) */
.detail-label {
  font-size: 12px;
  font-weight: 500;
  color: #717784;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Value / isi */
.detail-value {
  font-size: 15px;
  font-weight: 500;
  color: #1B1E28;
  line-height: 1.5;
  word-break: break-word;
}

/* Empty state */
.member-details + div p {
  font-size: 14px;
  color: #9AA0AE;
  text-align: center;
  margin: 16px 0 0;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .detail-value {
    font-size: 14px;
  }

  .detail-label {
    font-size: 11px;
  }
}

</style>
